import {
  afterNextRender,
  Component,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
  Injector,
  PLATFORM_ID,
  TemplateRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
  effect,
  untracked,
  computed,
} from '@angular/core';
import { isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';

import { filter, map, Subscription, tap, throttleTime } from 'rxjs';

import { breakpoints, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { findScrollContainer, isPresent, isDeepEqual, doLater } from '@fcom/core/utils/utils';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { quantityOfMonths, toMonthId } from '@fcom/core/utils/date';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { ScrollService } from '@fcom/common/services/scroll/scroll.service';
import { WindowRef } from '@fcom/core/providers/window';

import { Month, Day, DateSelection, CalendarViewModel } from '../../../utils/date.interface';
import { ButtonSize, ButtonTheme } from '../../buttons/button/enums';
import { IconButtonTheme, IconButtonSize } from '../../buttons/icon-button/enums';
import { CalendarService } from '../services/calendar.service';
import { ChipTheme } from '../../chip/interfaces';
import { CalendarNavigationType, CalendarNavigationEvent, DateRange } from '../interfaces';
import { areSelectedDatesChanged } from '../../../utils/date.utils';
import { UiLabels } from '../datepicker/datepicker.component';
import { IconButtonComponent } from '../../buttons/icon-button/icon-button.component';
import { ChipComponent } from '../../chip/chip.component';
import { MonthComponent } from '../month/month.component';

type CalendarDateLabels = Record<string, string>;

// TODO: calendar header height should be read dynamic
const CALENDAR_HEADER_HEIGHT = 208;
const CALENDAR_BOTTOM_THRESHOLD = 140;
@Component({
  selector: 'fcom-calendar',
  styleUrls: ['./calendar.component.scss'],
  templateUrl: './calendar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CalendarService],
  imports: [IconButtonComponent, ChipComponent, MonthComponent, NgClass, NgTemplateOutlet],
})
export class CalendarComponent implements OnDestroy, AfterViewInit {
  private element = inject(ElementRef);
  private calendarService = inject(CalendarService);
  private mediaQueryService = inject(MediaQueryService);
  private scrollService = inject(ScrollService);
  private platform = inject(PLATFORM_ID);
  private windowRef = inject(WindowRef);
  private injector = inject(Injector);

  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonSize = ButtonSize;
  public readonly ChipTheme = ChipTheme;
  public readonly IconButtonTheme = IconButtonTheme;
  public readonly IconButtonSize = IconButtonSize;
  public readonly SvgLibraryIcon = SvgLibraryIcon;
  public readonly CalendarNavigationType = CalendarNavigationType;
  public readonly CalendarNavigationEvent = CalendarNavigationEvent;

  calendarMonths = viewChild<ElementRef>('calendarMonths');
  tags = viewChildren('tag', { read: ElementRef });

  /**
   * Ranges where dates should be disabled
   * An array which includes array of two LocalDates
   */
  disabledDateRanges = input<DateRange[]>();

  /**
   * Starting day of the calendar
   * usually is TODAY
   */
  minDate = input.required<LocalDate>();

  /**
   * End day of the calendar
   * usually is TODAY + 360 days
   */
  maxDate = input.required<LocalDate>();

  /**
   * Initially preselected dates
   */
  selectedDates = input<DateSelection>();

  /**
   * Ways for navigation through months
   * arrows -> left and right arrows (single of dual month view)
   * mixed -> arrows used on desktop view and scroll used on mobile
   */
  navigationType = input(CalendarNavigationType.MIXED);

  /**
   * select range or single date
   */
  isDateRange = input(false);

  /**
   * Amount of months to be visible
   */
  displayMonths = input<1 | 2>(2);

  /**
   * Show month tag buttons on top of calendar
   */
  showTags = input(false);

  /**
   * Scroll on first selectable date on Init
   */
  scrollOnInit = input(true);

  /**
   * month index to be navigated to
   */
  scrollToMonthIndex = input<number>();

  /**
   * Whether the calendar is rendered inline on a page. When embedded there is no bounded scroll
   * container, so we do not want to scroll the whole page.
   */
  embedded = input<boolean>(false);

  /**
   * Object of translated and localized date labels
   * (e.g. month names, date formats)
   */
  dateLabels = input.required<CalendarDateLabels>();

  /**
   * Object of translated UI labels
   * (e.g. error messages, instructions)
   */
  uiLabels = input.required<UiLabels>();

  dayTemplate = input<TemplateRef<{ dayValue: Day; dayString: number }>>();

  monthChange = output<Month>();
  datesSelected = output<DateSelection>();
  visibleRangeChange = output<DateRange>();

  visibleMonthIndex: number;
  private readonly isMobile = this.mediaQueryService.isBreakpoint('tablet_down');
  readonly showEmptyWeeks = computed(() => {
    return !this.isMobile();
  });
  dataModel = signal<CalendarViewModel>(undefined);
  hoveredDate: LocalDate | null = null;

  private subscription: Subscription = new Subscription();
  private breakpoints = breakpoints;
  private mobileVisibleIndex = 0;
  private prevInputValues: Record<string, unknown> = {};
  private hasUserSelectedDate = false;
  private hasScrolledToInitialSelection = false;
  private visibleRange: DateRange;

  constructor() {
    effect(() => {
      const isMobile = this.isMobile();

      untracked(() => {
        this.calendarService.changeCalendarView(
          isMobile,
          (this.dataModel()?.isMobile !== isMobile && !isMobile
            ? this.dataModel()?.minDate?.plusMonths(this.mobileVisibleIndex)?.firstDayOfMonth
            : this.dataModel()?.focusDate) ?? this.minDate()
        );
      });
    });

    this.subscription.add(
      this.calendarService.dataModel$.subscribe((model) => {
        const isVerticalScroll = this.isVerticalScroll();
        const currentModel = this.dataModel();
        const newVisibleMonthIndex =
          model.months.find((month) => month.id === toMonthId(isVerticalScroll ? model.focusDate : model.firstDate))
            ?.monthArrayIndex ?? 0;

        const shouldUpdateMonth = this.visibleMonthIndex !== newVisibleMonthIndex;
        const shouldUpdateDatesSelected = areSelectedDatesChanged(currentModel?.selectedDates, model.selectedDates);
        const shouldScrollVerticalMonth = isVerticalScroll && currentModel?.lastDate?.gte(model.lastDate);

        this.dataModel.set(model);

        if (shouldUpdateMonth && isPlatformBrowser(this.platform)) {
          this.updateSelectedMonth(newVisibleMonthIndex, false, true);
        }

        if (shouldUpdateDatesSelected) {
          this.datesSelected.emit(model.selectedDates);
        }

        if (shouldScrollVerticalMonth && isPlatformBrowser(this.platform)) {
          this.updateSelectedMonth(this.mobileVisibleIndex, !shouldUpdateDatesSelected);
        }

        if (isPlatformBrowser(this.platform)) {
          this.adjustFocus();
          this.emitVisibleRangeIfChanged(model);
        }

        this.visibleMonthIndex = newVisibleMonthIndex ?? 0;

        if (
          isVerticalScroll &&
          this.scrollOnInit() &&
          !this.hasUserSelectedDate &&
          !this.hasScrolledToInitialSelection &&
          isPresent(model.minDate) &&
          isPresent(model.selectedDates?.startDate)
        ) {
          this.hasScrolledToInitialSelection = true;
          this.scrollToInitialSelection(model.selectedDates.startDate, model.minDate);
        }

        if (this.isVerticalScroll() && isPlatformBrowser(this.platform)) {
          afterNextRender(
            () => {
              if (this.isNearBottom() && this.hasMonthsLeftToLoad()) {
                this.loadNextMonths();
              }
            },
            { injector: this.injector }
          );
        }
      })
    );

    effect(() => {
      const inputs: Array<[string, unknown]> = [
        ['displayMonths', this.displayMonths()],
        ['isDateRange', this.isDateRange()],
        ['scrollOnInit', this.scrollOnInit()],
        ['showTags', this.showTags()],
        ['minDate', this.minDate()],
        ['maxDate', this.maxDate()],
        ['disabledDateRanges', this.disabledDateRanges()],
        ['selectedDates', this.selectedDates()],
        ['dateLabels', this.dateLabels()],
        ['navigationType', this.navigationType()],
        ['embedded', this.embedded()],
      ];

      const uiLabels = this.uiLabels();

      const data: Partial<CalendarViewModel> = {};

      for (const [key, value] of inputs) {
        if (!isDeepEqual(this.prevInputValues[key], value)) {
          (data as Record<string, unknown>)[key] = value;
          this.prevInputValues[key] = value;
        }
      }

      (data as Record<string, unknown>)['selectedLabel'] = uiLabels?.selected;
      this.calendarService.set(data);
    });

    effect(() => {
      const scrollToIndex = this.scrollToMonthIndex();
      if (isPresent(scrollToIndex) && isPresent(untracked(() => this.dataModel())?.minDate)) {
        untracked(() => this.selectMonth(scrollToIndex));
      }
    });
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platform)) {
      /**
       * Listens on calendarMonths scroll and act ONLY if it is mobile and navigationType is MIXED
       * and load more months when needed
       */
      this.subscription.add(
        this.scrollService
          .listen(this.calendarMonths())
          .pipe(
            filter(
              () =>
                this.isVerticalScroll() && this.windowRef.nativeWindow.innerWidth < parseInt(this.breakpoints.laptop)
            ),
            map(() => this.scrollContainer()),
            tap((container) => {
              // handle month scroll for histogram
              if (isPresent(this.distanceFromBottom(container))) {
                this.determineVisibleMonthWhenScroll(
                  container,
                  container.scrollTop,
                  container.clientHeight - CALENDAR_HEADER_HEIGHT
                );
              }
            }),
            filter(() => this.isNearBottom() && this.hasMonthsLeftToLoad()),
            throttleTime(100)
          )
          .subscribe(() => this.loadNextMonths())
      );
    }
  }

  /**
   * Arrow navigation
   * @param event
   * @param eventType CalendarNavigationEvent type
   */
  onNavigateEvent(event: Event, eventType: CalendarNavigationEvent): void {
    (event.currentTarget as HTMLElement).focus();

    const model = this.dataModel();

    if (!model?.firstDate) {
      return;
    }

    switch (eventType) {
      case CalendarNavigationEvent.PREV:
        this.calendarService.navigateToMonth(model.firstDate.minusMonths(1).firstDayOfMonth);
        break;
      case CalendarNavigationEvent.NEXT:
        this.calendarService.navigateToMonth(model.firstDate.plusMonths(1).firstDayOfMonth);
        break;
    }
  }

  /**
   * Histogram bar click or tag click
   * @param monthIndex
   */
  selectMonth(monthIndex: number): void {
    const { minDate } = this.dataModel();

    if (this.isVerticalScroll()) {
      this.handleVerticalScrollMonths(monthIndex + 1, minDate.plusMonths(monthIndex).firstDayOfMonth);
    } else {
      // navigate to the month by setting firstDate
      this.calendarService.navigateToMonth(minDate.plusMonths(monthIndex).firstDayOfMonth);
    }
  }

  /**
   * Select a day
   * @param day that is selected
   */
  selectDay(day: Day): void {
    if (!day.disabled) {
      this.hasUserSelectedDate = true;
      this.calendarService.selectDate(day.date);
    }
  }

  isRange(day: Day): boolean {
    const date = day.date;
    const { startDate, endDate } = this.dataModel()?.selectedDates ?? {};

    return (
      date.equals(startDate) ||
      date.equals(endDate) ||
      (date.gt(startDate) && date.lt(endDate)) ||
      (isPresent(startDate) && !isPresent(endDate) && date.gt(startDate) && date.lt(this.hoveredDate))
    );
  }

  isInside(day: Day): boolean {
    const date = day.date;
    const { startDate, endDate } = this.dataModel()?.selectedDates ?? {};
    return date.gt(startDate) && date.lt(endDate);
  }

  isHovered(day: Day): boolean {
    const date = day.date;
    const { startDate, endDate } = this.dataModel()?.selectedDates ?? {};
    return (
      this.isDateRange() &&
      isPresent(startDate) &&
      !isPresent(endDate) &&
      date.gt(startDate) &&
      date.lt(this.hoveredDate)
    );
  }

  private determineVisibleMonthWhenScroll(container: HTMLElement, scrollTop: number, containerHeight: number): void {
    const months = this.calendarMonths().nativeElement.children;

    let closestIndex = -1;
    let closestDistance = Number.MAX_VALUE;

    for (let i = 0; i < months.length; i++) {
      const month = months[i] as HTMLElement;
      const monthHeight = month.offsetHeight;

      const monthTop = month.offsetTop - CALENDAR_HEADER_HEIGHT;
      const monthBottom = monthTop + monthHeight;

      // calculate the distance to the viewport for the month
      let distanceToViewport: number;

      const isFirstAndScrolledToTop = i === 0 && scrollTop === 0 && monthBottom <= scrollTop + containerHeight;

      if (isFirstAndScrolledToTop || (scrollTop <= monthTop && monthBottom <= scrollTop + containerHeight)) {
        // month is fully visible in the viewport
        distanceToViewport = 0;
      } else if (monthTop <= scrollTop && scrollTop < monthBottom) {
        // month is partially visible at the top of the viewport
        distanceToViewport = scrollTop - monthTop;
      } else if (monthTop < scrollTop + containerHeight && scrollTop + containerHeight <= monthBottom) {
        // month is partially visible at the bottom of the viewport
        distanceToViewport = monthBottom - (scrollTop + containerHeight);
      } else {
        // month is outside the viewport
        distanceToViewport = Math.min(
          Math.abs(monthTop - scrollTop),
          Math.abs(monthBottom - (scrollTop + containerHeight))
        );
      }

      if (distanceToViewport <= closestDistance) {
        closestDistance = distanceToViewport;
        closestIndex = i;
      }

      if (closestDistance === 0) {
        if (scrollTop + containerHeight + CALENDAR_HEADER_HEIGHT < container.scrollHeight) {
          break;
        }
        closestIndex = months.length - 1;
      }
    }

    if (closestIndex !== this.mobileVisibleIndex) {
      // make sure that we report only once when month changes
      this.monthToChange(closestIndex, true);
    }
  }

  /**
   * Handle how months are displayed when it is vertical scroll
   * @param amount desired months
   */
  private handleVerticalScrollMonths(amount: number, focusDate?: LocalDate): void {
    const { months, minDate, maxDate } = this.dataModel();
    const maxMonths = quantityOfMonths(minDate, maxDate);
    const loadAmount = amount + 1 <= maxMonths ? amount + 1 : maxMonths;

    // month is already loaded, we need to adjust focus
    if (months.length >= loadAmount) {
      const monthIndex = isPresent(focusDate) ? amount - 1 : loadAmount - (months.length === loadAmount ? 1 : 2);
      this.updateSelectedMonth(monthIndex, true, true);
    } else {
      // desired month is not loaded, we need to load it first
      this.calendarService.loadMonths(loadAmount, focusDate);
      if (isPresent(focusDate)) {
        doLater(() => this.scrollToMonth(amount - 1));
      }
    }
  }

  private updateSelectedMonth(monthIndex: number, shouldScroll = true, shouldTriggerEmission = false): void {
    this.monthWillChange(monthIndex, shouldScroll);

    this.monthToChange(monthIndex, shouldTriggerEmission);
  }

  /**
   * This combines the two scroll methods for month change
   * one is the actual scroll to the needed month when navigationType is MIXED
   * other one is the scroll of the month chips if enabled
   */
  private monthWillChange(index: number, shouldScroll: boolean): void {
    const { months } = this.dataModel();
    // this is used only by the tags
    this.visibleMonthIndex = index;

    if (this.isVerticalScroll() && index >= 0 && index <= months.length - 1 && shouldScroll) {
      this.scrollToMonth(index);
    }

    if (this.showTags()) {
      this.scrollToMonthChip(index);
    }
  }

  /**
   * Emit month change event if monthIndex is present in loaded months
   * @param monthIndex
   * @param shouldTriggerEmission
   */
  private monthToChange(monthIndex: number, shouldTriggerEmission: boolean): void {
    const monthToChange = this.dataModel()?.months?.find((month) => month.monthArrayIndex === monthIndex);
    if (isPresent(monthToChange) && shouldTriggerEmission) {
      this.monthChange.emit(monthToChange);
      this.mobileVisibleIndex = monthIndex;
    }
  }

  private scrollToMonthChip(index: number): void {
    // TODO: try to find more elegant way of making sure the tags are present and not using setTimeout
    setTimeout(() => {
      this.tags()[index]?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 500);
  }

  private scrollToMonth(index: number): void {
    const monthElement = this.calendarMonths().nativeElement.children[index] as HTMLElement | undefined;
    // The target month may not be rendered yet (e.g. when triggered synchronously
    // during loadMonths). In that case skip; the deferred afterNextRender call handles it.
    if (isPresent(monthElement)) {
      this.scrollService.smoothScroll(monthElement, {
        offsetY: CALENDAR_HEADER_HEIGHT,
      });
    }
  }

  private scrollToInitialSelection(startDate: LocalDate, minDate: LocalDate): void {
    const monthIndex = quantityOfMonths(minDate, startDate.firstDayOfMonth) - 1;
    const monthsLoaded = this.dataModel()?.months.length ?? 0;
    this.calendarService.loadMonths(Math.max(monthIndex + 1, monthsLoaded));
    afterNextRender(() => this.scrollToMonth(monthIndex), { injector: this.injector });
  }

  private distanceFromBottom(container: HTMLElement | null): number | undefined {
    if (!container?.clientHeight || !container.scrollHeight) {
      return undefined;
    }

    return container.scrollHeight - container.scrollTop - container.clientHeight;
  }

  private loadNextMonths(): void {
    this.handleVerticalScrollMonths(this.dataModel()?.months.length + this.dataModel()?.displayMonths);
  }

  private isNearBottom(): boolean {
    const currentDistance = this.distanceFromBottom(this.scrollContainer());
    return isPresent(currentDistance) && currentDistance <= CALENDAR_BOTTOM_THRESHOLD;
  }

  private hasMonthsLeftToLoad(): boolean {
    const { months, minDate, maxDate } = this.dataModel() ?? {};
    return isPresent(months) && months.length < quantityOfMonths(minDate, maxDate);
  }

  private scrollContainer(): HTMLElement | null {
    return findScrollContainer(this.calendarMonths().nativeElement);
  }

  private isVerticalScroll(): boolean {
    return !!(this.dataModel()?.isMobile && this.dataModel()?.navigationType === CalendarNavigationType.MIXED);
  }

  private adjustFocus(): void {
    this.element.nativeElement
      .querySelector('button.day[tabindex="0"]')
      ?.focus({ preventScroll: this.isVerticalScroll() || this.embedded() });
  }

  private emitVisibleRangeIfChanged(model: CalendarViewModel): void {
    const dates = (model.months ?? [])
      .flatMap((month) => {
        return month?.weeks ?? [];
      })
      .flatMap((week) => {
        return week.days;
      })
      .filter((day) => {
        return isPresent(day?.id);
      })
      .map((day) => {
        return day.date;
      });

    if (dates.length === 0) {
      return;
    }

    const start = dates[0];
    const end = dates[dates.length - 1];
    if (start.id === this.visibleRange?.[0]?.id && end.id === this.visibleRange?.[1]?.id) {
      return;
    }

    this.visibleRange = [start, end];
    this.visibleRangeChange.emit(this.visibleRange);
  }
}
